/**
 * DatePicker - Date picker component using three select fields (month, day, year)
 *
 * Integrates with React Hook Form via Controller.
 * Similar to BirthdayPicker but for general date selection.
 *
 * @example
 * ```tsx
 * <Controller
 *   name="startDate"
 *   control={control}
 *   render={({ field }) => (
 *     <DatePicker
 *       value={field.value || { day: "", month: "", year: "" }}
 *       onChange={(val) => {
 *         field.onChange(val);
 *         field.onBlur();
 *       }}
 *       error={errors.startDate}
 *     />
 *   )}
 * />
 * ```
 */

"use client";

import { Box, Flex, FormControl, FormLabel } from "@chakra-ui/react";
import { useMemo } from "react";
import { Text } from "@/components/ui";
import { useStudioClock } from "@/hooks/shared/useStudioClock";
import { useThemeTokens } from "@/lib/theme/tokens";
import { formatYmdLocal } from "@/lib/utils/date/formatYmdLocal";
import { buildYearSelectOptions } from "@/lib/utils/date/year-select-options";
import { getErrorMessage } from "@/lib/utils/form";
import { CustomSelect } from "./CustomSelect";

export { formatYmdLocal };

export interface DateValue {
  day: string;
  month: string;
  year: string;
}

// Helper functions to convert between string (YYYY-MM-DD) and DateValue
// Option values in DatePicker are year string ("2025"), month/day String(1..31) without leading zero.
export const dateStringToValue = (
  dateStr: string | null | undefined,
): DateValue => {
  if (!dateStr || typeof dateStr !== "string")
    return { day: "", month: "", year: "" };
  const parts = dateStr.trim().split("-");
  if (parts.length !== 3) return { day: "", month: "", year: "" };
  const y = parts[0].trim();
  const m = parseInt(parts[1], 10);
  const d = parseInt(parts[2], 10);
  return {
    year: y,
    month: Number.isNaN(m) ? "" : String(m),
    day: Number.isNaN(d) ? "" : String(d),
  };
};

export const dateValueToString = (
  value: DateValue | null | undefined,
): string => {
  if (!value || !value.year || !value.month || !value.day) return "";
  return `${value.year}-${value.month.padStart(2, "0")}-${value.day.padStart(2, "0")}`;
};

function daysInMonth(year: number, month1Based: number): number {
  return new Date(year, month1Based, 0).getDate();
}

export interface DatePickerProps {
  /**
   * Current date value
   */
  value: DateValue;
  /**
   * Change handler
   */
  onChange: (value: DateValue) => void;
  /**
   * Error message
   */
  error?: { message?: string };
  /**
   * Whether field is required
   */
  required?: boolean;
  /**
   * Help text
   */
  help?: string;
  /**
   * Where help text sits. below-label explains the value before the selects.
   */
  helpPlacement?: "below-fields" | "below-label";
  /**
   * Label for the field
   */
  label?: string;
  /**
   * Minimum year (default: current year - 10)
   */
  minYear?: number;
  /**
   * Maximum year (default: current year + 10)
   */
  maxYear?: number;
  /**
   * Year dropdown order: "asc" (oldest first) or "desc" (newest first, e.g. for date of birth)
   */
  yearOrder?: "asc" | "desc";
  /**
   * When true, only past and today are selectable (caps year/month/day at local today).
   */
  disableFuture?: boolean;
  /**
   * When true, only today and future dates are selectable (floors year/month/day at local today).
   */
  disablePast?: boolean;
  /**
   * Earliest selectable date, YYYY-MM-DD. Combined with disablePast, the later floor wins.
   */
  minDate?: string;
  /**
   * Test ID prefix. Sub-selects will be testId-month, testId-day, testId-year.
   */
  testId?: string;
  /**
   * Tighter select widths for side-by-side date fields in modals.
   */
  density?: "default" | "compact";
  /**
   * Stretch month/day/year selects to fill the parent width.
   */
  fullWidth?: boolean;
  /**
   * When true, month/day/year selects cannot be changed.
   */
  isDisabled?: boolean;
}

const MONTHS_IN_YEAR = 12;

function withCurrentOption(
  options: { label: string; value: string }[],
  selected: string | undefined,
) {
  if (!selected || options.some((option) => option.value === selected)) {
    return options;
  }
  const numeric = Number(selected);
  if (Number.isNaN(numeric)) return options;
  return [
    { label: String(numeric).padStart(2, "0"), value: String(numeric) },
    ...options,
  ];
}

export const DatePicker = ({
  value,
  onChange,
  error,
  required = false,
  help,
  helpPlacement = "below-fields",
  label,
  minYear,
  maxYear,
  yearOrder = "asc",
  disableFuture = false,
  disablePast = false,
  minDate,
  testId,
  density = "default",
  fullWidth = false,
  isDisabled = false,
}: DatePickerProps) => {
  const theme = useThemeTokens();
  const studioClock = useStudioClock();
  const isCompact = density === "compact";
  const selectGap = isCompact ? "8px" : "10px";
  const monthDayWidth = isCompact ? "86px" : "100px";
  const yearWidth = isCompact ? "104px" : "120px";
  const monthDaySelectProps = fullWidth
    ? { flex: 1, minW: 0 }
    : {
        flex: { base: 1, md: "none" },
        w: { md: monthDayWidth },
        minW: 0,
      };
  const yearSelectProps = fullWidth
    ? { flex: 1.2, minW: 0 }
    : {
        flex: { base: 1.2, md: "none" },
        w: { md: yearWidth },
        minW: 0,
      };
  const currentYear = studioClock.year;

  const todayCaps = {
    y: studioClock.year,
    m: studioClock.month,
    d: studioClock.day,
  };
  const todayStr = studioClock.ymd;
  const minDateYmd =
    minDate && /^\d{4}-\d{2}-\d{2}$/.test(minDate) ? minDate : null;
  const floorYmd = [disablePast ? todayStr : null, minDateYmd]
    .filter((ymd): ymd is string => Boolean(ymd))
    .sort()
    .at(-1);
  const floor = floorYmd ? dateStringToValue(floorYmd) : null;
  const floorY = floor?.year ? Number(floor.year) : NaN;
  const floorM = floor?.month ? Number(floor.month) : NaN;
  const floorD = floor?.day ? Number(floor.day) : NaN;

  const min = minYear ?? currentYear;
  const maxRaw = maxYear ?? currentYear + 10;
  const max = disableFuture ? Math.min(maxRaw, todayCaps.y) : maxRaw;
  const effectiveMin = Number.isNaN(floorY) ? min : Math.max(min, floorY);

  const monthOptions = useMemo(() => {
    const base = Array.from({ length: MONTHS_IN_YEAR }, (_, i) => ({
      label: String(i + 1).padStart(2, "0"),
      value: String(i + 1),
    }));
    const selY = value.year ? Number(value.year) : NaN;
    if (disableFuture && !Number.isNaN(selY) && selY === todayCaps.y) {
      return base.filter((o) => Number(o.value) <= todayCaps.m);
    }
    if (!Number.isNaN(floorY) && !Number.isNaN(selY) && selY === floorY) {
      return withCurrentOption(
        base.filter((o) => Number(o.value) >= floorM),
        value.month,
      );
    }
    if (!Number.isNaN(floorY) && !Number.isNaN(selY) && selY < floorY) {
      return withCurrentOption([], value.month);
    }
    return withCurrentOption(base, value.month);
  }, [disableFuture, floorM, floorY, value.month, value.year, todayCaps.y, todayCaps.m]);

  const dayOptions = useMemo(() => {
    const selY = value.year ? Number(value.year) : NaN;
    const selM = value.month ? Number(value.month) : NaN;
    let count = 31;
    if (!Number.isNaN(selY) && !Number.isNaN(selM)) {
      count = daysInMonth(selY, selM);
      if (disableFuture && selY === todayCaps.y && selM === todayCaps.m) {
        count = Math.min(count, todayCaps.d);
      }
      if (
        !Number.isNaN(floorY) &&
        selY === floorY &&
        selM === floorM
      ) {
        count = count - floorD + 1;
      }
    }
    const startDay =
      !Number.isNaN(floorY) &&
      !Number.isNaN(selY) &&
      !Number.isNaN(selM) &&
      selY === floorY &&
      selM === floorM
        ? floorD
        : 1;
    return withCurrentOption(
      Array.from({ length: count }, (_, i) => ({
        label: String(startDay + i).padStart(2, "0"),
        value: String(startDay + i),
      })),
      value.day,
    );
  }, [
    disableFuture,
    floorD,
    floorM,
    floorY,
    value.year,
    value.month,
    value.day,
    todayCaps.y,
    todayCaps.m,
    todayCaps.d,
  ]);

  const yearOptions = useMemo(
    () =>
      buildYearSelectOptions({
        minYear: effectiveMin,
        maxYear: max,
        order: yearOrder,
        includeYear: value?.year,
      }),
    [effectiveMin, max, yearOrder, value?.year],
  );

  const handleChange = (part: "day" | "month" | "year", val: string) => {
    if (isDisabled) return;
    let next: DateValue = { ...value, [part]: val };
    const y = next.year ? Number(next.year) : NaN;
    const m = next.month ? Number(next.month) : NaN;
    let d = next.day ? Number(next.day) : NaN;
    if (!Number.isNaN(y) && !Number.isNaN(m)) {
      const dim = daysInMonth(y, m);
      if (!Number.isNaN(d)) {
        if (d > dim) d = dim;
        if (
          disableFuture &&
          y === todayCaps.y &&
          m === todayCaps.m &&
          d > todayCaps.d
        ) {
          d = todayCaps.d;
        }
        if (
          !Number.isNaN(floorY) &&
          y === floorY &&
          m === floorM &&
          d < floorD
        ) {
          d = floorD;
        }
        next = { ...next, day: String(d) };
      }
    }
    const ymd = dateValueToString(next);
    if (disableFuture && ymd) {
      if (ymd > todayStr) {
        next = dateStringToValue(todayStr);
      }
    }
    if (floorYmd && ymd && ymd < floorYmd) {
      next = dateStringToValue(floorYmd);
    }
    onChange(next);
  };

  // Extract error message from various possible error structures
  const errorMessage = getErrorMessage(
    error,
    undefined,
    ["day", "month", "year"],
    true,
  );
  const hasError = !!error;
  const helpBelowLabel = Boolean(help) && !errorMessage && helpPlacement === "below-label";
  const helpText = help ? (
    <Text variant="tertiary" fontSize="xs" lineHeight="1.5" whiteSpace="pre-line">
      {help}
    </Text>
  ) : null;

  return (
    <FormControl isInvalid={hasError} isDisabled={isDisabled} w={fullWidth ? "100%" : undefined}>
      {label && (
        <FormLabel
          fontSize="sm"
          fontWeight="500"
          mb={helpBelowLabel ? 1 : "5px"}
          color={theme.text.primary}
          letterSpacing="-0.01em"
        >
          {label}
          {required && (
            <Text as="span" color={theme.accent.primary} ml={1}>
              *
            </Text>
          )}
        </FormLabel>
      )}

      {helpBelowLabel ? <Box mb={2}>{helpText}</Box> : null}

      <Flex gap={selectGap} flexWrap="nowrap" w={fullWidth ? "100%" : undefined} maxW="100%">
        <CustomSelect
          key="month"
          options={monthOptions}
          placeholder="MM"
          value={value?.month ? String(value.month) : ""}
          onChange={(val) => handleChange("month", val)}
          formControlProps={{
            ...monthDaySelectProps,
            isInvalid: hasError,
            isDisabled,
          }}
          data-testid={testId ? `${testId}-month` : undefined}
        />
        <CustomSelect
          key="day"
          options={dayOptions}
          placeholder="DD"
          value={value?.day ? String(value.day) : ""}
          onChange={(val) => handleChange("day", val)}
          formControlProps={{
            ...monthDaySelectProps,
            isInvalid: hasError,
            isDisabled,
          }}
          data-testid={testId ? `${testId}-day` : undefined}
        />
        <CustomSelect
          key="year"
          options={yearOptions}
          placeholder="YYYY"
          value={value?.year ? String(value.year) : ""}
          onChange={(val) => handleChange("year", val)}
          formControlProps={{
            ...yearSelectProps,
            isInvalid: hasError,
            isDisabled,
          }}
          data-testid={testId ? `${testId}-year` : undefined}
        />
      </Flex>

      {errorMessage && (
        <Text
          color={theme.accent.primary}
          fontSize="xs"
          fontWeight="500"
          mt={1.5}
        >
          {errorMessage}
        </Text>
      )}

      {help && !errorMessage && !helpBelowLabel ? (
        <Box mt={1.5}>{helpText}</Box>
      ) : null}
    </FormControl>
  );
};
